/** @jsxImportSource @emotion/react */
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Link,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ReactNode } from 'react';
import { StylesClassName, UseStyles } from 'types/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ITemplate } from 'types/dto';
import Accordion from 'components/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TemplatesIcon } from 'components/TemplatesItem/TemplatesIcon';
import { FormattedMessage } from 'react-intl';
import { wordWrap } from 'utils/accessability';
import { nanoid } from 'nanoid';
import { config } from 'application';
import * as ga from 'application/ga';

interface TemplatesItemProps {
  className?: StylesClassName;
  item: ITemplate;
  onDownload: (id: number) => void;
  icon?: ReactNode;
}

const TemplatesItem: React.FC<TemplatesItemProps> = ({ className, item, onDownload, icon }) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);

  return (
    <Accordion className={[classes.TemplatesItem, className]}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <TemplatesIcon code={item.code} color="primary" style={{ marginInlineEnd: theme.spacing(4) }} />
        {item.name}
      </AccordionSummary>
      <AccordionDetails>
        <Box pb={7} css={classes.details}>
          <Box p={4} css={classes.content}>
            {item.isComingLater ? (
              <Typography component="p" variant="body1">
                Coming later
              </Typography>
            ) : (
              <>
                {item.details.length > 0 &&
                  item.details.map(item => (
                    <Typography key={nanoid()} component="p" variant="body1" pb={2}>
                      <span dangerouslySetInnerHTML={{ __html: item.description }} />
                      {item.source && (
                        <>
                          {' (Source: '}
                          <Link href={item.source} target="_blank">
                            {item.source}
                          </Link>
                          {')'}
                        </>
                      )}
                    </Typography>
                  ))}

                {item.note && (
                  <Typography component="p" variant="body1" mt={4}>
                    <i dangerouslySetInnerHTML={{ __html: item.note }} />
                  </Typography>
                )}

                {item.resources && !!item.resources.length && (
                  <Typography component="ul" variant="body1" mt={6}>
                    {item.resources.map((resource, index) => (
                      <li key={index}>
                        <Link href={resource.link} target="_blank" css={classes.resourceLink}>
                          {resource.name ?? resource.link}
                        </Link>{' '}
                        {resource.description}
                      </li>
                    ))}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {!item.isComingLater && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box p={4} pl={6} pr={6} css={classes.actions}>
                <a
                  css={classes.downloadLink}
                  href={`${config.nip.apiUrl}/plans/types/${item.id}/template`}
                  onClick={() => ga.downloadTemplate(item)}
                  download
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ whiteSpace: 'nowrap' }}
                    endIcon={<FileDownloadOutlinedIcon />}
                  >
                    <FormattedMessage id={'TemplatesItem.download'} defaultMessage={'Download Template'} />
                  </Button>
                </a>
              </Box>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  TemplatesItem: {},
  details: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  content: {
    ...wordWrap,

    ul: {
      paddingInlineStart: theme.spacing(4),
    },
    li: {
      '&::marker': {
        fontSize: 12,
      },
    },
    a: {
      fontWeight: 600,
      textDecoration: 'none',
      wordBreak: 'break-word',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  actions: {},
  resourceLink: {
    ...wordWrap,
    wordBreak: 'break-word',
  },
  downloadLink: {
    textDecoration: 'none',
  },
});

export default TemplatesItem;
